import React, { useEffect, useRef, useState } from "react";
import CommentModal from "./CommentModal";

/**
 * props:
 * - image: { id, src, title, date }
 * - savedData: { image, strokes: [...], comments: [...] }  OR null
 * - onUpdate(data) -> called whenever strokes/comments change (autosave to parent)
 *
 * Behavior:
 * - Canvas uses natural image size for drawing/co-ords. The .canvasWrap is positioned inside a clip area to allow pan/drag.
 * - Mark checkbox toggles drawing mode (when checked -> draw, when unchecked -> pan)
 * - After completing a stroke, we open comment modal to capture text; pin is placed at last stroke point.
 * - Pins and strokes persist per-image via onUpdate callback.
 */
const CanvasArea = ({ image, savedData = null, onUpdate = () => {} }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
  };
  
  const previewWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const [imgObj, setImgObj] = useState(null);
  const [isMarking, setIsMarking] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [strokes, setStrokes] = useState([]);
  const [comments, setComments] = useState([]);
  const [pendingCommentPosition, setPendingCommentPosition] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);

  // pan state
  const panState = useRef({ isPanning: false, start: { x: 0, y: 0 }, offset: { x: 0, y: 0 } });

  // load image & optional savedData
  useEffect(() => {
    
    const img = new Image();
    img.src = `${process.env.REACT_APP_API_URL}/photoshoot/photo/full/${image.fname}`;
    //img.src = image.src;
    img.onload = () => {
      setImgObj(img);
      // apply saved data if any
      if (image.va_feedback != '') {
        /* var feedbackJson = JSON.parse(image.va_feedback).recceImages[0];
        console.log(feedbackJson);
        setStrokes(feedbackJson.strokes || []);
        setComments(feedbackJson.comments || []); */

        try {
            var feedbackJson = JSON.parse(image.va_feedback).photoshootImages[0];
            setStrokes(feedbackJson.strokes || []);
            setComments(feedbackJson.comments || []);
        } catch (e) {
            setStrokes([]);
            setComments([]);
        }

      } else {
        setStrokes([]);
        setComments([]);
      }
    };
    // cleanup on unmount
    return () => {
      setImgObj(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image.id, image.src]);

  // Setup canvas size and draw image + existing strokes/pins
  useEffect(() => {
    if (!imgObj || !canvasRef.current || !canvasWrapRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas natural size to image natural size
    canvas.width = imgObj.naturalWidth || imgObj.width;
    canvas.height = imgObj.naturalHeight || imgObj.height;

    // Reset transform / position
    canvas.style.width = "auto";
    canvas.style.height = "auto";

    // Draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgObj, 0, 0, canvas.width, canvas.height);

    // Draw saved strokes
    if (strokes && strokes.length) {
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "red";
      strokes.forEach((stroke) => {
        ctx.beginPath();
        stroke.forEach((p, idx) => {
          if (idx === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
      });
    }

    // (pins are DOM elements placed inside canvasWrap on renderComments below)
    renderComments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgObj, strokes, comments]);

  // whenever strokes/comments update, notify parent
  useEffect(() => {
    if (!image) return;
    onUpdate({
      image: image.src,
      strokes,
      comments,
    });
  }, [strokes, comments, image, onUpdate]);

  // comment pin rendering (DOM pins inside canvasWrap)
  const renderComments = () => {
    const container = canvasWrapRef.current;
    if (!container) return;
    // remove existing pins/tooltips
    Array.from(container.querySelectorAll(".va-comment-pin, .va-comment-tooltip")).forEach((el) => el.remove());

    comments.forEach((c, idx) => {
      const pin = document.createElement("div");
      pin.className = "va-comment-pin comment-pin";
      pin.style.left = `${c.x}px`;
      pin.style.top = `${c.y}px`;
      pin.setAttribute("data-idx", idx);

      const tooltip = document.createElement("div");
      tooltip.className = "va-comment-tooltip comment-tooltip";
      tooltip.style.left = `${c.x + 16}px`;
      tooltip.style.top = `${c.y - 6}px`;
      tooltip.textContent = c.text;

      // show tooltip on hover
      pin.addEventListener("mouseenter", () => (tooltip.style.display = "block"));
      pin.addEventListener("mouseleave", () => (tooltip.style.display = "none"));

      container.appendChild(pin);
      container.appendChild(tooltip);
    });
  };

  // Convert client coords to canvas (image) coords
  const clientToCanvas = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const wrap = previewWrapRef.current;
    if (!canvas || !wrap) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    // Note: canvas is placed inside canvasWrap which may be translated by pan offsets.
    // rect already reflects the current position.
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    // clamp
    return { x: Math.max(0, Math.min(canvas.width, Math.round(x))), y: Math.max(0, Math.min(canvas.height, Math.round(y))) };
  };

  // Pointer events for drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = previewWrapRef.current;
    if (!canvas || !wrap) return;

    let pointerId = null;

    const onPointerDown = (e) => {
      // if marking is enabled -> start drawing
      if (isMarking) {
        e.preventDefault();
        drawingStart(e);
        try {
          e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId);
          pointerId = e.pointerId;
        } catch {}
      } else {
        // start pan
        const target = canvasWrapRef.current || canvas;
        panState.current.isPanning = true;
        panState.current.start.x = e.clientX;
        panState.current.start.y = e.clientY;
        target.style.cursor = "grabbing";
      }
    };

    const onPointerMove = (e) => {
      if (isMarking && drawing) {
        drawingMove(e);
      } else if (!isMarking && panState.current.isPanning) {
        panMove(e);
      }
    };

    const onPointerUp = (e) => {
      if (isMarking && drawing) {
        drawingEnd(e);
        try {
          e.target.releasePointerCapture && e.target.releasePointerCapture(e.pointerId);
          pointerId = null;
        } catch {}
      } else {
        // end pan
        panEnd(e);
      }
    };

    wrap.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      wrap.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarking, drawing, imgObj]);

  // drawing helpers
  const drawingStart = (e) => {
    setDrawing(true);
    setCurrentStroke([]);
    const pos = clientToCanvas(e.clientX, e.clientY);
    setCurrentStroke([pos]);

    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const drawingMove = (e) => {
    const pos = clientToCanvas(e.clientX, e.clientY);
    setCurrentStroke((prev) => {
      const next = [...prev, pos];
      // draw incremental
      const ctx = canvasRef.current.getContext("2d");
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "red";
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      return next;
    });
  };

  const drawingEnd = (e) => {
    setDrawing(false);
    const pos = clientToCanvas(e.clientX, e.clientY);
    setCurrentStroke((prev) => {
      const finalStroke = [...prev, pos];
      if (finalStroke.length > 1) {
        setStrokes((s) => {
          const next = [...s, finalStroke];
          return next;
        });
      }
      // open comment modal with last position
      setPendingCommentPosition(pos);
      setCommentModalOpen(true);
      return [];
    });
  };

  // Pan helpers - move canvasWrap (absolute)
  const panMove = (e) => {
    const target = canvasWrapRef.current || canvasRef.current;
    const { isPanning, start, offset } = panState.current;
    if (!isPanning) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    let newX = offset.x + dx;
    let newY = offset.y + dy;

    // simple clamp so it doesn't go too far (basic)
    const wrap = previewWrapRef.current;
    const panTarget = canvasWrapRef.current || canvasRef.current;
    const minX = Math.min(0, wrap.clientWidth - panTarget.offsetWidth);
    const minY = Math.min(0, wrap.clientHeight - panTarget.offsetHeight);
    newX = Math.max(minX, Math.min(0, newX));
    newY = Math.max(minY, Math.min(0, newY));

    panTarget.style.left = `${newX}px`;
    panTarget.style.top = `${newY}px`;
  };

  const panEnd = (e) => {
    const target = canvasWrapRef.current || canvasRef.current;
    if (!panState.current.isPanning) return;
    panState.current.isPanning = false;
    panState.current.offset.x = parseInt(target.style.left || "0", 10) || 0;
    panState.current.offset.y = parseInt(target.style.top || "0", 10) || 0;
    target.style.cursor = isMarking ? "crosshair" : "grab";
  };

  // comment modal handlers
  const handleSaveComment = (text) => {
    if (text && pendingCommentPosition) {
      const newComment = { x: pendingCommentPosition.x, y: pendingCommentPosition.y, text };
      setComments((c) => {
        const next = [...c, newComment];
        return next;
      });
    }
    setPendingCommentPosition(null);
    setCommentModalOpen(false);
  };

  const handleCancelComment = () => {
    setPendingCommentPosition(null);
    setCommentModalOpen(false);
  };

  // when comments state changes, re-render pins (DOM)
  useEffect(() => {
    renderComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  // checkbox toggle for marking mode
  useEffect(() => {
    const panTarget = canvasWrapRef.current || canvasRef.current;
    if (!panTarget) return;
    panTarget.style.cursor = isMarking ? "crosshair" : "grab";
  }, [isMarking]);

  // Provide a small UI above canvas: Mark toggle and caption
  return (
    <>
      <div className="previewWrap">
        {/* <div className="markCheckbox mb-2">
          <label className="flex items-center space-x-2">
            <input type="checkbox" id="markCheckbox" className="w-4 h-4" checked={isMarking} onChange={(e) => setIsMarking(e.target.checked)} />
            <span className="text-gray-700">Mark</span>
          </label>
        </div> */}

        <div ref={previewWrapRef} className="preview-img-wrap relative inline-block" id="previewWrap" style={{ overflow: "hidden" }}>
          {/* canvasWrap positions the canvas and pins absolutely so pan works */}
          <div ref={canvasWrapRef} className="canvasWrap relative" style={{ position: "absolute", left: 0, top: 0 }}>
            <canvas ref={canvasRef} className="drawCanvas" style={{ display: "block", height: "auto" }} />
            {/* pins are inserted into canvasWrap as DOM elements */}
          </div>
        </div>
      </div>

      <div className="preview-caption mt-2 flex justify-between items-center">
        <div className="caption-text">{image.title}</div>
        <div className="text-sm text-gray-600">{image.date}</div>
      </div>

      {/* Comment modal */}
      <CommentModal open={commentModalOpen} initialText="" onSave={handleSaveComment} onCancel={handleCancelComment} />

      {/* Inline styles for comment pins/tooltips (scoped class names used) */}
      <style js>{`
        /* Using same class names as original to reuse your CSS if present */
        .comment-pin,
        .va-comment-pin {
          width: 14px;
          height: 14px;
          background: red;
          border-radius: 50%;
          border: 2px solid white;
          position: absolute;
          transform: translate(-50%, -50%);
          cursor: pointer;
          z-index: 3;
        }
        .comment-tooltip,
        .va-comment-tooltip {
          position: absolute;
          background: #333;
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          white-space: nowrap;
          display: none;
          z-index: 4;
        }
      `}</style>
    </>
  );
};

export default CanvasArea;
